import { z } from 'zod';
import { InstantSchema, QrCodeSchema } from './common.schema';
import { KitSchema } from './kit.schema';
import { JobOrderSchema } from './jobOrder.schema';

/**
 * `GET /kits/{kitId}/print`, section 7.7.
 *
 * Everything the A4 sheet needs in one call, so printing works from a cold
 * cache on a phone by a bench. The QR is rendered client side from `qr.value`;
 * the server does not ship an image.
 *
 * The lab block is the lab's NAME and nothing else. The address, the telephone
 * number and the email address came off the sheet with the request of
 * 13 September 2026, section 5: the client reads a job sheet on the floor, not
 * in the post, and the letterhead was three lines of ink nobody used. Nothing
 * but the sheet was reading them, so they leave the contract with the sheet.
 *
 * Calling this endpoint writes an activity entry of type `print`.
 */
export const PrintPayloadSchema = z.object({
  kit: KitSchema,
  jobOrder: JobOrderSchema,
  qr: z.object({
    value: QrCodeSchema,
    /**
     * The printed code is the bare value string. Not a URL: a URL is longer, it
     * degrades scan reliability on a toner starved print, and it puts the host
     * name on every tray. Section 11.3.
     */
    format: z.literal('text'),
  }),
  lab: z.object({
    name: z.string(),
  }),
  printedAt: InstantSchema,
  printedByUserName: z.string(),
});

export type PrintPayload = z.infer<typeof PrintPayloadSchema>;
